import { FetchError } from "@/utils/customFetch";
import { useMe } from "@/utils/hooks/useMe";
import { UserProfile } from "@/utils/types/response/me";
import { useParams, useRouter, usePathname } from "next/navigation";
import { useEffect } from "react";

export function useStartup() {
  const router = useRouter();
  const pathname = usePathname();
  const { companyId } = useParams();

  const { data, isPending, error } = useMe();

  const hasAccessToCompany = (user: UserProfile) => {
    const { companies, hasAccessToAllCompanies } = user;

    return (
      hasAccessToAllCompanies ||
      companies.some((company) => company.id === companyId)
    );
  };

  const redirectIfNoAccessToCompany = (user: UserProfile) => {
    const { companies } = user;

    if (hasAccessToCompany(user)) {
      return;
    }

    const redirectPath =
      companies.length === 1 ? `/${companies[0].id}/dashboard` : "/";

    router.replace(redirectPath);
  };

  useEffect(() => {
    if (data?.user) {
      redirectIfNoAccessToCompany(data.user);
    }
  }, [router, companyId, data]);

  const isUnauthorized = () => {
    return error instanceof FetchError && error.status === 401;
  };

  const isUserWithoutAccessToCompany = () => {
    return pathname !== "/" && data?.user && !hasAccessToCompany(data.user);
  };

  const isSingleCompanyUserOnRoot = () => {
    if (data?.user && data.user.hasAccessToAllCompanies) return false;

    return pathname === "/" && data?.user && data.user.companies.length === 1;
  };

  const isLoading =
    isPending ||
    isUnauthorized() ||
    isUserWithoutAccessToCompany() ||
    isSingleCompanyUserOnRoot();

  return isLoading;
}
